/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View, type StyleProp, type ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { plural } from '../format/plural';
import { colors, spacing, ticketColors, ticketFonts } from '../theme/public-api';

type FilterSheetButtonProps = {
  activeCount: number;
  onOpen: () => void;
  accessibilityLabel?: string;
  /** testID para E2E. */
  testID?: string;
  style?: StyleProp<ViewStyle>;
  /** `desk`: PC transparente. `ticket`: mobile full-width proto. */
  variant?: 'default' | 'desk' | 'ticket';
  /** Desk: reemplaza el label default (`Filtros` / `Filtros · N`). */
  deskLabel?: string;
  /** Desk: borde/fondo activo (ej. categoría desde Filtros). */
  deskActive?: boolean;
};

export function FilterSheetButton({
  activeCount,
  onOpen,
  accessibilityLabel = 'Abrir panel de filtros',
  testID,
  style,
  variant = 'default',
  deskLabel,
  deskActive = false,
}: FilterSheetButtonProps) {
  const hasActive = activeCount > 0;

  if (variant === 'desk') {
    const label = deskLabel ?? (hasActive ? `Filtros · ${activeCount}` : 'Filtros');
    const emphasized = deskActive || hasActive;
    return (
      <TouchableOpacity
        style={[styles.deskButton, emphasized && styles.deskButtonActive, style]}
        onPress={onOpen}
        activeOpacity={0.55}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={hasActive ? `${activeCount} ${plural(activeCount, 'filtro activo', 'filtros activos')}` : undefined}
        testID={testID}
      >
        <Text style={styles.deskLabel}>{label}</Text>
      </TouchableOpacity>
    );
  }

  if (variant === 'ticket') {
    return (
      <TouchableOpacity
        style={[styles.ticketButton, style]}
        onPress={onOpen}
        activeOpacity={0.55}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={hasActive ? `${activeCount} ${plural(activeCount, 'filtro activo', 'filtros activos')}` : undefined}
        testID={testID}
      >
        <Text style={styles.ticketLabel}>Filtros</Text>
        {hasActive ? (
          <View style={styles.ticketBadge}>
            <Text style={styles.ticketBadgeText}>{activeCount}</Text>
          </View>
        ) : null}
        <View style={styles.ticketSpacer} />
        <Ionicons name="chevron-down" size={14} color={ticketColors.ink3} />
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity
      style={[styles.button, style]}
      onPress={onOpen}
      activeOpacity={0.55}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={hasActive ? `${activeCount} ${plural(activeCount, 'filtro activo', 'filtros activos')}` : undefined}
      testID={testID}
    >
      <View style={styles.left}>
        <Ionicons name="filter-outline" size={18} color={colors.textSecondary} />
        <Text style={styles.label}>Filtros</Text>
        {hasActive ? (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{activeCount}</Text>
          </View>
        ) : null}
      </View>
      <Ionicons name="chevron-down" size={14} color={colors.textMuted} />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.borderSoft,
    backgroundColor: colors.surface,
    paddingHorizontal: 14,
    marginBottom: 14,
    alignSelf: 'stretch',
  },
  deskButton: {
    // A11y: ≥44px target (antes 42).
    height: 44,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0,
    alignSelf: 'auto',
  },
  deskButtonActive: {
    borderColor: ticketColors.brandBorder,
    backgroundColor: ticketColors.brandSoft,
  },
  deskLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  ticketButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    // A11y: ≥44px target (antes 42).
    height: 44,
    paddingHorizontal: 12,
    marginBottom: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignSelf: 'stretch',
  },
  ticketLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  ticketBadge: {
    minWidth: 18,
    height: 18,
    paddingHorizontal: 5,
    borderRadius: 99,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ticketBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.mono,
    lineHeight: 14,
  },
  ticketSpacer: {
    flex: 1,
  },
  left: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  badge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 7,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '800',
    color: ticketColors.goldInk,
  },
});
